import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useState } from 'react';

interface IAssetOptionsTagInputProps {
  value: string[];
  onChange: (options: string[]) => void;
  canHandleEdit?: boolean;
  placeholder?: string;
}

/**
 * Tag-style editor for select/enum field options.
 * Type a value and press Enter (or comma) to add it as a removable tag.
 * Pasting comma/newline-separated text adds one tag per value.
 */
const AssetOptionsTagInput: React.FC<IAssetOptionsTagInputProps> = ({
  value,
  onChange,
  canHandleEdit = true,
  placeholder = 'Type an option and press Enter…',
}) => {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const parts = raw
      .split(/[\n,]/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return;

    const next = [...value];

    parts.forEach((part) => {
      if (!next.some((o) => o.toLowerCase() === part.toLowerCase())) {
        next.push(part);
      }
    });
    onChange(next);
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(text);
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    commit(text + e.clipboardData.getData('text'));
  };

  return (
    <div
      className='asset-tag-input'
      role='presentation'
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((option) => (
        <span key={option} className='asset-tag-input__tag'>
          {option}
          <button
            type='button'
            className='asset-tag-input__remove'
            aria-label={`Remove ${option}`}
            onClick={(e) => {
              e.stopPropagation();
              onChange(value.filter((o) => o !== option));
            }}
            disabled={!canHandleEdit}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className='asset-tag-input__field'
        value={text}
        placeholder={value.length ? '' : placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(text)}
        onPaste={handlePaste}
        disabled={!canHandleEdit}
      />
    </div>
  );
};

export default AssetOptionsTagInput;
